import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { AMBIENCES, getAmbience, getEngine } from "@/lib/ambientEngine";

const STORAGE_KEY = "confilex.ambient.v1";

// Mapping rétrocompatible entre anciens IDs et nouveaux univers de marque.
const LEGACY_AMBIENCE_MAP = {
  "piano-juridique": "cabinet-confidentiel",
  "bibliotheque": "bibliotheque-juridique",
  "neoclassique": "piano-neoclassique",
  "piano-contemporain": "concentration-elegante",
  "lounge-institutionnel": "salon-prive",
};

const AmbientSoundContext = createContext(null);

export function AmbientSoundProvider({ children }) {
  const engineRef = useRef(null);
  const [ambienceId, setAmbienceId] = useState(AMBIENCES[0].id);
  const [volume, setVolume] = useState(0.4);
  const [enabled, setEnabled] = useState(false);
  const [interacted, setInteracted] = useState(false); // user gesture obtenu
  const [hasBeenInvited, setHasBeenInvited] = useState(false);
  // Mode "Réunion en cours" : timestamp (ms) jusqu'auquel le son reste coupé.
  // null = pas de pause manuelle active. enabled reste vrai pour reprise auto.
  const [muteUntil, setMuteUntil] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [nowPlaying, setNowPlaying] = useState(null);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);

  // Branche l'engine pour notifier les changements de piste + état lecture
  useEffect(() => {
    if (!engineRef.current) engineRef.current = getEngine();
    engineRef.current.onTrackChange((meta) => setNowPlaying(meta));
    engineRef.current.onPlaybackStateChange(({ blocked }) => setPlaybackBlocked(blocked));
  }, []);

  // Rafraîchit la progression chaque seconde quand le son joue
  useEffect(() => {
    if (!enabled || !interacted) return undefined;
    const t = setInterval(() => {
      if (engineRef.current) {
        const np = engineRef.current.getNowPlaying();
        if (np) setNowPlaying(np);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [enabled, interacted, ambienceId]);

  // Tick par seconde pour mettre à jour le compte à rebours UI et déclencher la reprise auto
  useEffect(() => {
    if (!muteUntil) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [muteUntil]);

  // Levée auto du mute une fois la durée écoulée
  useEffect(() => {
    if (muteUntil && now >= muteUntil) {
      setMuteUntil(null);
    }
  }, [muteUntil, now]);

  // Charge les préférences depuis localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved.ambienceId) {
        const remapped = LEGACY_AMBIENCE_MAP[saved.ambienceId] || saved.ambienceId;
        if (AMBIENCES.some((a) => a.id === remapped)) {
          setAmbienceId(remapped);
        }
      }
      if (typeof saved.volume === "number") setVolume(saved.volume);
      if (typeof saved.enabled === "boolean") setEnabled(saved.enabled);
      if (saved.invited) setHasBeenInvited(true);
      if (typeof saved.muteUntil === "number" && saved.muteUntil > Date.now()) {
        setMuteUntil(saved.muteUntil);
      }
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[CONFILEX ambient] preferences read failed", err);
      }
    }
  }, []);

  // Persiste les préférences
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ambienceId, volume, enabled, invited: hasBeenInvited, muteUntil,
      }));
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[CONFILEX ambient] preferences write failed", err);
      }
    }
  }, [ambienceId, volume, enabled, hasBeenInvited, muteUntil]);

  // Démarre / arrête le moteur audio en fonction de l'état
  useEffect(() => {
    if (!engineRef.current) engineRef.current = getEngine();
    const engine = engineRef.current;
    const muted = muteUntil && muteUntil > Date.now();
    if (enabled && interacted && !muted) {
      engine.start(getAmbience(ambienceId), volume);
    } else {
      engine.stop({ fade: true });
    }
  }, [enabled, interacted, ambienceId, volume, muteUntil]);

  // Mise à jour du volume "à chaud"
  useEffect(() => {
    if (engineRef.current && enabled && interacted && !(muteUntil && muteUntil > Date.now())) {
      engineRef.current.setVolume(volume);
    }
  }, [volume, enabled, interacted, muteUntil]);

  // Pause auto quand l'onglet est masqué, reprise au retour
  useEffect(() => {
    const onVisibility = () => {
      if (!engineRef.current) return;
      const muted = muteUntil && muteUntil > Date.now();
      if (document.hidden) {
        engineRef.current.stop({ fade: true });
      } else if (enabled && interacted && !muted) {
        engineRef.current.start(getAmbience(ambienceId), volume);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [enabled, interacted, ambienceId, volume, muteUntil]);

  // Détection d'autres sources audio sur la page (videos / élements <audio>)
  useEffect(() => {
    const checkOtherAudio = () => {
      const all = document.querySelectorAll("audio, video");
      let playing = false;
      all.forEach((el) => {
        if (el.dataset?.confilexAmbient) return;
        if (!el.paused && !el.muted && el.currentTime > 0) playing = true;
      });
      const engine = engineRef.current;
      if (!engine) return;
      const muted = muteUntil && muteUntil > Date.now();
      if (playing) {
        engine.stop({ fade: true });
      } else if (enabled && interacted && !document.hidden && !muted) {
        engine.start(getAmbience(ambienceId), volume);
      }
    };
    document.addEventListener("play", checkOtherAudio, true);
    document.addEventListener("pause", checkOtherAudio, true);
    document.addEventListener("ended", checkOtherAudio, true);
    return () => {
      document.removeEventListener("play", checkOtherAudio, true);
      document.removeEventListener("pause", checkOtherAudio, true);
      document.removeEventListener("ended", checkOtherAudio, true);
    };
  }, [enabled, interacted, ambienceId, volume, muteUntil]);

  // Capture un geste utilisateur (pré-requis navigateur)
  const acknowledgeGesture = useCallback(() => {
    setInteracted(true);
  }, []);

  const toggleEnabled = useCallback((forceOn) => {
    // 1) Unlock SYNCHRONE de l'AudioContext (obligatoire Safari/iOS)
    try {
      if (!engineRef.current) engineRef.current = getEngine();
      engineRef.current.unlock();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[CONFILEX ambient] engine unlock failed", err);
      }
    }
    setInteracted(true);
    setHasBeenInvited(true);
    setMuteUntil(null);

    // 2) Décide du nouvel état
    const next = typeof forceOn === "boolean" ? forceOn : !enabled;
    setEnabled(next);

    // 3) Démarre/arrête le moteur SYNCHRONIQUEMENT dans ce handler.
    //    Crucial : le geste utilisateur est valide ICI, pas dans un useEffect
    //    appelé après React re-render (les navigateurs bloquent autoplay sinon).
    try {
      if (next) {
        engineRef.current.start(getAmbience(ambienceId), volume);
      } else {
        engineRef.current.stop({ fade: true });
      }
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[CONFILEX ambient] engine start/stop in handler failed", err);
      }
    }
  }, [enabled, ambienceId, volume]);

  // Démarre une pause "Réunion en cours" pour N minutes (reprise auto ensuite)
  const startMeetingMute = useCallback((minutes) => {
    if (!minutes || minutes <= 0) {
      setMuteUntil(null);
      return;
    }
    setMuteUntil(Date.now() + minutes * 60 * 1000);
    setInteracted(true);
    setHasBeenInvited(true);
  }, []);

  const cancelMeetingMute = useCallback(() => setMuteUntil(null), []);

  // -----------------------------------------------------------------------
  // Détecteur de visioconférence intelligent.
  // On wrap discrètement getUserMedia / getDisplayMedia. Dès qu'une caméra,
  // un micro ou un partage d'écran est demandé, on active automatiquement
  // un mode "Réunion en cours" de 60 min (uniquement si l'ambiance est ON
  // et que la pause n'est pas déjà active). Quand toutes les pistes média
  // s'arrêtent, on lève le mute pour reprendre l'ambiance.
  // -----------------------------------------------------------------------
  const activeStreamsRef = useRef(0);
  const autoMutedRef = useRef(false);
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices) return undefined;
    const md = navigator.mediaDevices;
    const originalGUM = md.getUserMedia ? md.getUserMedia.bind(md) : null;
    const originalGDM = md.getDisplayMedia ? md.getDisplayMedia.bind(md) : null;

    const attachStreamWatcher = (stream) => {
      if (!stream || typeof stream.getTracks !== "function") return;
      const tracks = stream.getTracks();
      if (!tracks.length) return;
      activeStreamsRef.current += 1;
      if (enabled && !(muteUntil && muteUntil > Date.now())) {
        autoMutedRef.current = true;
        setMuteUntil(Date.now() + 60 * 60 * 1000); // 60 min par défaut
      }
      let aliveCount = tracks.length;
      const onTrackEnded = () => {
        aliveCount -= 1;
        if (aliveCount <= 0) {
          activeStreamsRef.current = Math.max(0, activeStreamsRef.current - 1);
          // si plus aucun stream actif et que c'est nous qui avions muté → reprendre
          if (activeStreamsRef.current === 0 && autoMutedRef.current) {
            autoMutedRef.current = false;
            setMuteUntil(null);
          }
        }
      };
      tracks.forEach((t) => t.addEventListener("ended", onTrackEnded, { once: true }));
    };

    if (originalGUM) {
      md.getUserMedia = async (constraints) => {
        const stream = await originalGUM(constraints);
        try {
          attachStreamWatcher(stream);
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("[CONFILEX ambient] stream watcher failed (getUserMedia)", err);
          }
        }
        return stream;
      };
    }
    if (originalGDM) {
      md.getDisplayMedia = async (constraints) => {
        const stream = await originalGDM(constraints);
        try {
          attachStreamWatcher(stream);
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            console.warn("[CONFILEX ambient] stream watcher failed (getDisplayMedia)", err);
          }
        }
        return stream;
      };
    }
    return () => {
      // Restauration propre si le provider est démonté
      if (originalGUM) md.getUserMedia = originalGUM;
      if (originalGDM) md.getDisplayMedia = originalGDM;
    };
  }, [enabled, muteUntil]);

  const meetingActive = !!(muteUntil && muteUntil > now);
  const meetingRemainingSec = meetingActive ? Math.max(0, Math.round((muteUntil - now) / 1000)) : 0;
  const meetingAuto = meetingActive && autoMutedRef.current;

  // ---------------------------------------------------------------------------
  // Auto-ducking : abaisse le volume quand une voix parle (utilisateur ou IA),
  // quand l'utilisateur saisit du texte, ou pour toute "raison" déclarée par
  // un composant via useAmbientDucking(). Plusieurs raisons peuvent coexister :
  // le volume ne remonte qu'une fois TOUTES les raisons levées.
  // ---------------------------------------------------------------------------
  const [duckReasons, setDuckReasons] = useState(() => new Set());
  const [currentScene, setCurrentScene] = useState(null);

  const requestDuck = useCallback((reason, multiplier = 0.25) => {
    if (!engineRef.current || !reason) return;
    setDuckReasons((prev) => {
      const next = new Set(prev);
      next.add(reason);
      return next;
    });
    engineRef.current.duck(multiplier, 350);
  }, []);

  const releaseDuck = useCallback((reason) => {
    if (!reason) return;
    setDuckReasons((prev) => {
      if (!prev.has(reason)) return prev;
      const next = new Set(prev);
      next.delete(reason);
      if (next.size === 0 && engineRef.current) {
        engineRef.current.unduck(600);
      }
      return next;
    });
  }, []);

  const value = {
    AMBIENCES,
    ambienceId,
    setAmbienceId,
    volume,
    setVolume,
    enabled,
    toggleEnabled,
    acknowledgeGesture,
    hasBeenInvited,
    setHasBeenInvited,
    startMeetingMute,
    cancelMeetingMute,
    meetingActive,
    meetingRemainingSec,
    meetingAuto,
    nowPlaying,
    playbackBlocked,
    // Ducking & scenes (multi-canaux : workspace, voice AI, upload, …)
    requestDuck,
    releaseDuck,
    duckedReasons: Array.from(duckReasons),
    currentScene,
    setCurrentScene,
  };

  return (
    <AmbientSoundContext.Provider value={value}>
      {children}
    </AmbientSoundContext.Provider>
  );
}

export function useAmbientSound() {
  const ctx = useContext(AmbientSoundContext);
  if (!ctx) throw new Error("useAmbientSound must be used within AmbientSoundProvider");
  return ctx;
}
